import { useEffect, useState } from 'react';
import { api, ApiError, type MatchEventWire } from '../api-client.js';
import { type SeatLabelMap } from '../lib/seat-labels.js';
import { formatAflEvent } from '../lib/afl/format-event.js';

export type EventLogProps = {
  matchId: string;
  /** seatId → "You" / "Opponent" map so log entries don't show raw UUIDs. */
  seatLabels?: SeatLabelMap;
  /** Auto-refresh cadence; 0 disables polling. */
  pollMs?: number;
  /**
   * 'collapsible' (default) — compact recent strip + expandable full log.
   * 'fill' — always open, newest first, fills its container and scrolls
   * INTERNALLY (the match sidebar uses this so the panels above never
   * scroll out of view).
   */
  layout?: 'collapsible' | 'fill';
  /**
   * Per-game narrator. Defaults to the AFL one, which returns null for
   * anything it doesn't know — so a game without a narrator here shows
   * an empty log rather than raw payloads.
   */
  format?: (
    type: string,
    payload: Record<string, unknown>,
    seatLabels?: SeatLabelMap
  ) => string | null;
};

/**
 * Match-commentary panel. Renders ONLY narrated calls (plain English) — the
 * engine's structured events become sentences via formatAflEvent; housekeeping
 * events (which it narrates as null) are dropped, and raw key=value payloads
 * are NEVER shown. No code surfaces in the app.
 */
export function EventLog({
  matchId,
  seatLabels,
  pollMs = 2000,
  layout = 'collapsible',
  format = formatAflEvent
}: EventLogProps) {
  const [events, setEvents] = useState<MatchEventWire[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(layout === 'fill');

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setInterval> | null = null;
    const fetchOnce = () => {
      api
        .listEvents(matchId)
        .then((data) => {
          if (alive) setEvents(data);
        })
        .catch((e) => {
          if (alive) setError(e instanceof ApiError ? e.message : String(e));
        });
    };
    fetchOnce();
    if (pollMs > 0) timer = setInterval(fetchOnce, pollMs);
    return () => {
      alive = false;
      if (timer) clearInterval(timer);
    };
  }, [matchId, pollMs]);

  const fill = layout === 'fill';

  // NEWEST FIRST (owner directive), narrated-only: every line is a plain
  // sentence; un-narrated housekeeping events are dropped here.
  const narrated = [...(events ?? [])]
    .reverse()
    .map((e) => ({ e, line: format(e.type, e.payload, seatLabels) }))
    .filter((x): x is { e: MatchEventWire; line: string } => x.line !== null);
  const count = narrated.length;
  const recent = fill ? [] : narrated.slice(0, 3);

  const list = narrated.length > 0 && (
    <ol className={`space-y-1 overflow-y-auto pr-1 ${fill ? 'min-h-0 flex-1' : 'max-h-72'}`}>
      {narrated.map(({ e, line }) => (
        <li
          key={e.id}
          className="flex items-baseline gap-2 rounded-md border border-ec-line/70 bg-ec-ground/50 px-2 py-1 text-[11px]"
        >
          <span className="font-display tabular-nums text-ec-bone-dim">#{e.sequence}</span>
          <span className="font-serif text-ec-face">{line}</span>
          <span className="ml-auto shrink-0 font-display tabular-nums text-ec-bone-dim" title={e.createdAt}>
            {formatTimeShort(e.createdAt)}
          </span>
        </li>
      ))}
    </ol>
  );

  if (fill) {
    // The match sidebar's only scrollable region: header pinned, newest
    // event right under it, list scrolls inside the panel.
    return (
      <section className="ec-surface flex h-full min-h-0 flex-col overflow-hidden">
        <div className="ec-surface__head shrink-0 bg-gradient-to-r from-ec-oxblood-deep to-ec-oxblood">
          <h3 className="ec-surface__title">
            Commentary{events ? ` · ${count}` : ''}
          </h3>
          <span className="font-display text-[10px] font-semibold uppercase tracking-wide text-ec-face/80">newest first</span>
        </div>
        <div className="flex min-h-0 flex-1 flex-col p-2">
          {!events && !error && <p className="text-xs text-ec-bone-dim">Loading the call…</p>}
          {error && narrated.length === 0 && <p className="text-xs text-ec-bone-dim">Reconnecting to the match feed…</p>}
          {events && !error && narrated.length === 0 && <p className="text-xs text-ec-bone-dim">No events yet.</p>}
          {list}
        </div>
      </section>
    );
  }

  return (
    <section className="ec-surface overflow-hidden">
      {recent.length > 0 && (
        <ul className="border-b border-ec-line px-4 py-2 font-serif text-xs">
          {recent.map(({ e, line }, i) => (
            <li
              key={e.id}
              className={
                i === 0
                  ? 'animate-deal-in text-ec-face motion-reduce:animate-none'
                  : 'text-ec-bone-dim'
              }
            >
              <span className="mr-1 text-ec-brass">{i === 0 ? '▸' : ' '}</span>
              {line}
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-2 text-sm transition-colors hover:bg-ec-panel-2"
        aria-expanded={open}
      >
        <span className="font-display font-medium uppercase tracking-wide text-ec-bone">
          Full commentary{events ? ` · ${count}` : ''}
        </span>
        <span className="text-xs text-ec-brass">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="border-t border-ec-line px-4 py-3">
          {!events && !error && (
            <p className="text-xs text-ec-bone-dim">Loading the call…</p>
          )}
          {error && narrated.length === 0 && (
            <p className="text-xs text-ec-bone-dim">Reconnecting to the match feed…</p>
          )}
          {events && !error && narrated.length === 0 && (
            <p className="text-xs text-ec-bone-dim">No events yet.</p>
          )}
          {list}
        </div>
      )}
    </section>
  );
}

function formatTimeShort(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso.slice(11, 19);
  }
}
